import { type CrudList, crudStoreFactory, type Nullable, type PastaCrudStoreState } from "@pasta/box-v2";
import { type HomeDto } from "../dto/home.dto";
import {
  type LetterFileCloneDto,
  type LetterFileDto,
  type LetterFileSearchDto,
  type TransferLfDto,
} from "../dto/letter-file.dto";
import { useLetterFileApi } from "../http/letter-file.api";
import md5 from "md5";
import { LinkPurpose } from "../dto/letter-file-document.dto";

export interface LetterFileStoreState extends PastaCrudStoreState<LetterFileDto> {
  chronoMap: Map<string, LetterFileDto>;
  home: HomeDto;
  currentId?: Nullable<number>;
  balaWhiteList: string[];
}

export const useLetterFileStore = crudStoreFactory("letterFile", useLetterFileApi, (storeDef, api) => ({
  ...storeDef,
  state: (): LetterFileStoreState => {
    return {
      ...storeDef.state(),
      chronoMap: new Map(),
      home: {
        todo: { data: [], total: 0 },
        current: { data: [], total: 0, draft: 0 },
        done: { data: [], total: 0, archived: 0 },
      },
      currentId: null,
      balaWhiteList: [],
    };
  },
  actions: {
    ...storeDef.actions,
    setList(list: CrudList<LetterFileDto>) {
      this.list = list;
      this.setMaps(list.data);
    },
    setMaps(letterFiles: LetterFileDto[]) {
      this.$patch(state => {
        for (const lf of letterFiles) {
          state.map.set(lf.id, lf);
          state.chronoMap.set(lf.chronoNumber, lf);
        }
      });
    },
    setOne(element: LetterFileDto) {
      this.$patch(state => {
        state.map.set(element.id, element);
        state.chronoMap.set(element.chronoNumber, element);
      });
    },
    deleteCached(ids: number[]) {
      this.$patch(state => {
        for (const id of ids) {
          const chronoNumber = state.map.get(id)?.chronoNumber;
          state.map.delete(id);
          if (chronoNumber) {
            state.chronoMap.delete(chronoNumber);
          }
        }
        state.list.data = state.list.data.filter(element => !ids.includes(element.id));
      });
    },
    async fetchOneByChrono(chronoNumber: string) {
      this.setOne(await api.getByChrono(chronoNumber));
      return this.chronoMap.get(chronoNumber)!;
    },
    async fetchCurrent(force = true) {
      if (this.currentId) {
        const letterFileDto = await this.fetchOne(this.currentId, force);
        const indexInHome = this.home.current.data.findIndex(lf => lf.id === letterFileDto.id);
        if (indexInHome === -1) {
          this.home.current.data.push(letterFileDto);
          this.home.current.total += 1;
        } else {
          this.home.current.data[indexInHome] = letterFileDto;
        }
      }
    },
    async fetchOne(id: number, force?: boolean): Promise<LetterFileDto> {
      let xVersion: string = "";
      const lf: LetterFileDto | undefined = this.map.get(id);
      if (lf) {
        xVersion = md5(lf.modificationDate, {});
      }
      const result = await api.fetchById(id, force ? undefined : { xVersion });
      if (result) {
        this.map.set(id, result);
        this.chronoMap.set(result.chronoNumber, result);
        return result;
      }
      return lf as LetterFileDto;
    },
    async getHome() {
      const home = await api.getHome();
      this.home = home;
      this.setMaps([...home.todo.data, ...home.current.data, ...home.done.data]);
    },
    async search(dto: LetterFileSearchDto, pagination: { page: number; pageSize: number }) {
      const offset = (pagination.page - 1) * pagination.pageSize;
      const limit = pagination.pageSize;
      this.setList(await api.search(dto, { offset, limit }));
    },
    async undraft(id: number) {
      const result = await api.undraft(id);
      this.setOne(result);
    },
    async next(id: number) {
      const result = await api.next(id);
      this.setOne(result);
    },
    async take(id: number) {
      const result = await api.take(id);
      this.setOne(result);
    },
    async appendDownstream(id: number, mail: string) {
      await api.appendDownstream(id, mail);
    },
    async emptyDownstream(id: number) {
      await api.emptyDownstream(id);
    },
    async reorderDownstream(id: number, downstream: number[]) {
      await api.reorderDownstream(id, downstream);
    },
    async removeDownstream(id: number, downstreamId: number) {
      await api.removeDownstream(id, downstreamId);
    },
    async appendDownstreamWithPosition(id: number, mail: string, position: number) {
      await api.appendDownstream(id, mail, position);
    },
    async reorderFolders(id: number, folders: number[]) {
      await api.reorderFolders(id, folders);
    },
    async removeAllDocs(id: number) {
      await api.removeAllDocs(id);
    },
    async applyFlux(id: number, fluxId: number) {
      await api.applyFlux(id, fluxId);
    },
    async generateArchive(id: number, purpose?: LinkPurpose) {
      return api.generateArchive(id, purpose);
    },
    async clone(id: number, body: LetterFileCloneDto) {
      return api.clone(id, body);
    },
    async unarchive(id: number) {
      return api.unarchive(id);
    },
    async countTransferCandidates(dto: TransferLfDto) {
      return api.countTransferCandidates(dto);
    },
    async transfer(dto: TransferLfDto) {
      return api.transfer(dto);
    },
    async exportTransferCandidates(dto: TransferLfDto) {
      return api.exportTransferCandidates(dto);
    },
    setCurrentId(value: number | null) {
      this.currentId = value;
    },
    getCurrent() {
      return this.currentId ? this.map.get(this.currentId) : null;
    },
    setBalaWhiteList(balaWhiteList: string[]) {
      this.balaWhiteList = balaWhiteList.map(item => item.toLowerCase());
    },
  },
}));
